import { useMemo } from "react";
import {
  useDataSources,
  SOURCE_DEFS,
  isActive,
  inactiveSources,
  defaultActive,
  setActiveSynced,
  useServerDataSources,
} from "../../stores/dataSources";
import { useFieldCovariates, useHaneyPoints } from "../../hooks/queries";
import type { CollectKey } from "./CollectActions";
import type { DashboardField } from "../../types/contract";

// The per-field DATA SOURCES cell — shown in the table's "Field data" COLUMN, which the "Add Field
// Data" button toggles — correction: a new column, not drawer). One row per source: checkbox +
// layer estimate; environmental sources checked automatically; unchecking keeps the data but marks
// it INACTIVE (forces manual weights in soil-property mapping).
export function FieldSourcesCell({
  field,
  onExpand,
}: {
  field: DashboardField;
  onExpand: (k: CollectKey) => void;
}) {
  const { data: covs } = useFieldCovariates(field.field_id);
  const overrides = useDataSources((s) => s.overrides);
  useServerDataSources(field); // hydrate from the account-level server flags (server wins)

  // Count only layers that actually HOLD DATA on this field (has_values) — the catalog lists every
  // stack column, so a raw count would show "243 layers" of machine data on a field with none —
  // caught this live.
  const countByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of covs ?? [])
      if (c.has_values) m.set(c.category, (m.get(c.category) ?? 0) + 1);
    return m;
  }, [covs]);

  const inactive = inactiveSources(overrides, field);

  // Not a model-gating source (kriged for display), so it gets an add/manage link, no active
  // checkbox.
  const haneyCount = useHaneyPoints(field.field_id).data?.length ?? 0;

  return (
    <div className="min-w-56">
      <ul className="space-y-0.5">
        {SOURCE_DEFS.map((d) => {
          const active = isActive(overrides, field, d.key);
          const hasData = defaultActive(field, d.key);
          const isInactive = !active && (d.manual ? hasData : true);
          // populated -> the real count; a manual source with NO data on this field shows
          // only a clearly-marked potential ("~240 if pulled"), never a hard number.
          const populated =
            d.key === "samples"
              ? (field.soil_samples?.collected ?? 0)
              : d.category != null
                ? (countByCategory.get(d.category) ?? 0)
                : 0;
          const count =
            populated > 0
              ? d.key === "samples"
                ? `${populated.toLocaleString()} pts in`
                : `${populated.toLocaleString()} layers`
              : d.manual
                ? `${d.fallbackEstimate.replace(" layers", "")} if pulled`
                : d.fallbackEstimate;
          return (
            <li key={d.key} className="flex items-center justify-between gap-2">
              <label className="flex min-w-0 cursor-pointer items-center gap-1 text-[10px]">
                <input
                  type="checkbox"
                  checked={active}
                  aria-label={d.label}
                  onChange={(e) => {
                    setActiveSynced(field, d.key, e.target.checked); // local + server PATCH (rolls back on failure)
                    if (e.target.checked && d.manual && !hasData)
                      onExpand(d.key as CollectKey);
                  }}
                />
                <span className="truncate">{d.label}</span>
                {isInactive && (
                  <span
                    title="Data stays in the system but is excluded from use — the AI mapping model can't run on partial data, so manual weights become required."
                    className="rounded bg-amber-400/15 px-1 text-[9px] text-amber-300"
                  >
                    inactive
                  </span>
                )}
              </label>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="text-[9px] tabular-nums text-faint">
                  {count}
                </span>
                {d.manual && (
                  <button
                    className="text-[9px] text-muted underline decoration-dotted hover:text-fg"
                    onClick={() => onExpand(d.key as CollectKey)}
                    aria-label={`${d.label} options`}
                  >
                    {hasData ? "manage" : "add"}
                  </button>
                )}
              </span>
            </li>
          );
        })}
        {/* Haney test — manual import, add/manage opens the Haney panel (no model-gating checkbox) */}
        <li className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-[10px]">Haney Test</span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="text-[9px] tabular-nums text-faint">
              {haneyCount > 0
                ? `${haneyCount.toLocaleString()} pts in`
                : "import"}
            </span>
            <button
              className="text-[9px] text-muted underline decoration-dotted hover:text-fg"
              onClick={() => onExpand("haney" as CollectKey)}
              aria-label="Haney test options"
            >
              {haneyCount > 0 ? "manage" : "add"}
            </button>
          </span>
        </li>
      </ul>
      {inactive.length > 0 && (
        <p className="mt-1 text-[9px] text-amber-300/90">
          {inactive.length} inactive — soil mapping requires manual weights.
        </p>
      )}
    </div>
  );
}
